<template>
  <FriedDropdown
    placement="bottom-end"
    :open="isCommentHistoryOpen"
    :main-axis-offset="8"
    :cross-axis-offset="8"
    :close-on-action="false"
    @update:open="isCommentHistoryOpen = $event"
  >
    <template #trigger>
      <FriedButton
        data-external="signing-page-top-bar-comment-history-button"
        class="hidden-xs-and-down comment-history-button"
        :button-type="ButtonType.Tertiary"
        :disabled="disabled"
      >
        <template #leftIcon>
          <FriedIcon icon="comment-history" />
        </template>
        {{ $t('cc_comment_history') }}
      </FriedButton>
      <FriedIconButton
        data-external="signing-page-top-bar-comment-history-button"
        class="hidden-xs-and-up"
        :disabled="disabled"
      >
        <FriedIcon icon="comment-history" />
      </FriedIconButton>
    </template>
    <template #content>
      <CommentHistory
        v-show="isXSmallScreenAndUp"
        class="comment-history-container"
        :comments="comments"
        :current-user="currentUser"
        :recipients="recipients"
        :selected-id="selectedCommentId"
        :hovered-id="hoveredCommentId"
        :sender="sender"
        :read-only="preventCommentsModification"
        :comments-hidden="commentsHidden"
        :show-hide-comments-toggle="true"
        @create-reply="$event => handleCreateReply($event)"
        @select="select"
        @update="$event => updateComment($event)"
        @go-to-comment="$event => goToComment($event)"
        @click-outside="handleClickOutside"
        @toggle-comments="toggleComments"
      />
      <FriedModal v-if="isXSmallScreenAndDown && isCommentHistoryOpen" @close="hide">
        <template #header>
          <FriedModalHeader :header="t('cc_comment_history')" />
        </template>
        <CommentHistory
          hide-header
          :comments="comments"
          :current-user="currentUser"
          :recipients="recipients"
          :selected-id="selectedCommentId"
          :hovered-id="hoveredCommentId"
          :sender="sender"
          :read-only="preventCommentsModification"
          :comments-hidden="commentsHidden"
          :show-hide-comments-toggle="true"
          @create-reply="handleCreateReply"
          @select="select"
          @update="updateComment"
          @go-to-comment="select"
          @click-outside="handleClickOutside"
          @toggle-comments="toggleComments"
        />
      </FriedModal>
    </template>
  </FriedDropdown>
</template>

<script lang="ts">
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import CommentHistory from '@getaccept/lib-shared-new/src/contextual-commenting/components/CommentHistory.vue';
import { useContextualCommentingStore } from '@getaccept/lib-shared-new/src/contextual-commenting/store/contextual-commenting.store';
import type { CreateReplyInput } from '@getaccept/lib-shared-new/src/contextual-commenting/types/create-reply-input';
import type { CurrentUser } from '@getaccept/lib-shared-new/src/contextual-commenting/types/current-user';
import type { Recipient } from '@getaccept/lib-shared-new/src/types/Recipient';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import { ButtonType } from '@getaccept/fried-tofu';
import type { PropType, Ref } from 'vue';
import { computed, defineComponent, toRefs } from 'vue';
import { storeToRefs } from 'pinia';
import type { CommentParticipant } from '@getaccept/lib-shared-new/src/contextual-commenting/types/comment-participant';
import { useI18n } from 'vue-i18n';
import { useContextualCommentingData } from '../composables/contextual-commenting-data.composable';

export default defineComponent({
  components: {
    CommentHistory,
  },
  props: {
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    preventCommentsModification: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const { recipient }: { recipient: Ref<SigningPageRecipient> } = toRefs(props);

    const { updateComment, createReply, selectComment } = useContextualCommentingData();
    const { isXSmallScreenAndUp, isXSmallScreenAndDown } = useBreakpoints();

    const handleCreateReply = (payload: CreateReplyInput) => {
      const currentUser: CommentParticipant = { userId: null, recipientId: recipient.value.id };
      createReply(payload, currentUser);
    };

    const store = useContextualCommentingStore();

    const {
      commentHistoryComments: comments,
      selectedCommentId,
      hoveredCommentId,
      isCommentHistoryOpen,
      commentsHidden,
    } = storeToRefs(store);

    const currentUser = computed<CurrentUser>(() => ({
      recipientId: recipient.value.id,
      name: recipient.value.fullName,
      thumbUrl: recipient.value.thumbUrl,
    }));

    const isSelected = (commentId: string) => commentId === selectedCommentId.value;

    const select = (commentId: string) => {
      if (isSelected(commentId)) {
        return;
      }
      hide();
      selectComment(commentId, props.recipient.id);
    };

    const handleClickOutside = (commentId: string) => {
      const ignoreClickOutside = !isSelected(commentId) || !isCommentHistoryOpen.value;
      if (ignoreClickOutside) {
        return;
      }

      select('');
    };

    const hide = () => {
      store.toggleCommentHistory(false);
    };

    const goToComment = (commentId: string) => {
      emit('go-to-comment', commentId);
      hide();
    };

    return {
      select,
      updateComment,
      handleCreateReply,
      goToComment,
      t,
      hide,
      handleClickOutside,
      toggleComments: store.toggleHideComments,

      ButtonType,
      comments,
      currentUser,
      selectedCommentId,
      hoveredCommentId,
      isCommentHistoryOpen,
      isXSmallScreenAndUp,
      isXSmallScreenAndDown,
      commentsHidden,
    };
  },
});
</script>
<style lang="scss" scoped>
.comment-history-button {
  &.ga-tertiary {
    background-color: var(--white);
  }
}

.comment-history-container {
  z-index: 1;
  padding: var(--spacing-100);
  width: 360px;
}

.comments-history-controls {
  position: relative;

  .unread-comments-indicator {
    position: absolute;
    background-color: var(--blue-45);
    height: 8px;
    width: 8px;
    border-radius: 50%;
    top: var(--spacing-25);
    right: var(--spacing-25);
  }
}
</style>
